import React, { useMemo, useRef, useState } from 'react';
import type { RabEntry } from '../../types';
import type { Planungsfrage } from '../planungsphase/types';
import { datumDeutsch } from '../planungsphase/unterlagenLogic';
import { markiereUebernommen } from '../../services/planungsphaseService';
import { describeError } from '../../services/errorMessage';
import { eintragAusFrage, fragenAusPlanung, type UfsaFrageVorschlag } from './ufsaPlanung';
import { AlertTriangle, CheckCircle2, ClipboardList, EyeOff, Loader2, Plus } from 'lucide-react';

interface Props {
    fragen: readonly Planungsfrage[];
    bereit: boolean;
    /** Lesefehler der Planungsphase — dann gibt es keine Karten, aber einen Grund. */
    fehler: string | null;
    isReadOnly?: boolean;
    /** Vorbelegt das Eintragsformular; gespeichert wird dort durch den Nutzer. */
    onVorbelegen: (eintrag: Omit<RabEntry, 'id'>, frage: UfsaFrageVorschlag) => void;
    /** Planungsdaten neu lesen, nachdem ein Stempel gesetzt wurde. */
    onNeuLaden: () => void;
}

/**
 * Karten „Aus der Planungsphase" im Massnahmenkatalog: beantwortete Fragen
 * mit Ziel „Unterlage" (Kapitel J, N).
 *
 *   „Als Eintrag übernehmen" — belegt das Formular vor UND stempelt die Frage
 *                              (`uebernommen_am`, Entscheid E7). Gespeichert
 *                              wird der Eintrag erst durch den Nutzer.
 *   „Erledigt / ausblenden"  — stempelt nur; die Erkenntnis steht schon in
 *                              der Unterlage oder gehoert nicht hinein.
 *
 * Gestempelte Karten bleiben gedimmt sichtbar — der Stempel ist Teil der
 * Akte, kein Loeschen. Der Stempel ist ein Schreibzugriff auf die
 * Planungsphase; scheitert er (Recht, Netz), steht der Grund hier, nie ein
 * Browser-Dialog.
 *
 * Solange EIN Stempel laeuft, sind ALLE Knoepfe gesperrt: Uebernahme und
 * Stempel gehoeren zusammen, und eine zweite Karte darf nicht ihr Formular
 * fuellen, waehrend ihr Stempel still ausbleibt. Die Sperre liegt zusaetzlich
 * in einem Ref, damit zwei Klicks vor dem naechsten Zeichnen nicht beide
 * durchkommen.
 */
export const UfsaPlanungFragenKarten: React.FC<Props> = ({ fragen, bereit, fehler, isReadOnly = false, onVorbelegen, onNeuLaden }) => {
    const vorschlaege = useMemo(() => fragenAusPlanung(fragen), [fragen]);
    const [laeuft, setLaeuft] = useState<string | null>(null);
    const [stempelFehler, setStempelFehler] = useState<string | null>(null);
    const sperreRef = useRef(false);
    const gesperrt = laeuft !== null;

    /**
     * Stempelt die Frage. `true`, wenn sie danach gestempelt IST — auch wenn
     * `markiereUebernommen` 0 Zeilen meldet (eine andere Sitzung war schneller,
     * der erste Stempel bleibt). `false` nur bei einem Fehler; der steht dann
     * an der Karte.
     */
    const stempeln = async (v: UfsaFrageVorschlag): Promise<boolean> => {
        sperreRef.current = true;
        setLaeuft(v.id);
        setStempelFehler(null);
        try {
            await markiereUebernommen([v.id], new Date().toISOString());
            onNeuLaden();
            return true;
        } catch (e) {
            setStempelFehler(`${v.code}: ${describeError(e)}`);
            return false;
        } finally {
            sperreRef.current = false;
            setLaeuft(null);
        }
    };

    /** „Erledigt / ausblenden" — nur stempeln. */
    const erledigt = (v: UfsaFrageVorschlag) => {
        if (sperreRef.current || v.uebernommenAm) return;
        void stempeln(v);
    };

    /**
     * „Als Eintrag übernehmen" — ERST stempeln, DANN vorbelegen. Scheitert der
     * Stempel, bleibt das Formular leer: sonst liesse sich ein Eintrag
     * speichern, dessen Frage in der Planungsphase als nicht uebernommen gilt.
     * Schon gestempelt (erneut vorbelegen): nur vorbelegen, kein zweiter Stempel.
     */
    const uebernehmen = async (v: UfsaFrageVorschlag) => {
        if (sperreRef.current) return;
        if (!v.uebernommenAm) {
            const gestempelt = await stempeln(v);
            if (!gestempelt) return;
        }
        onVorbelegen(eintragAusFrage(v), v);
    };

    if (!bereit) return null;
    if (!vorschlaege.length && !fehler) return null;

    const offen = vorschlaege.filter(v => !v.uebernommenAm).length;

    return (
        <section className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="px-5 py-4 border-b border-slate-100 flex items-start gap-3">
                <div className="p-2 rounded-lg bg-emerald-100 text-emerald-700 shrink-0">
                    <ClipboardList className="w-5 h-5" />
                </div>
                <div className="min-w-0">
                    <h3 className="text-lg font-bold text-slate-800 flex items-center gap-2">
                        Aus der Planungsphase
                        <span className="text-xs text-slate-400 bg-slate-50 px-2 py-0.5 rounded-full border border-slate-200 font-medium">
                            {offen} offen · {vorschlaege.length - offen} übernommen
                        </span>
                    </h3>
                    <p className="text-xs text-slate-500 mt-0.5">
                        Beantwortete Fragen zur Unterlage für spätere Arbeiten. „Als Eintrag übernehmen" belegt das Formular unten vor — gespeichert wird erst mit „Eintrag hinzufügen".
                    </p>
                </div>
            </div>

            <div className="p-5 space-y-3">
                {fehler && (
                    <div role="alert" className="flex items-start gap-2 text-xs text-amber-900 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                        <AlertTriangle className="w-4 h-4 shrink-0 mt-px" />
                        <span>Planungsphase nicht vollständig gelesen: {fehler}</span>
                    </div>
                )}
                {stempelFehler && (
                    <div role="alert" className="flex items-start gap-2 text-xs text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                        <AlertTriangle className="w-4 h-4 shrink-0 mt-px" />
                        <span>Übernahme konnte nicht gestempelt werden — {stempelFehler} Das Formular wurde nicht vorbelegt; die Karte bleibt offen, bitte erneut versuchen.</span>
                    </div>
                )}

                {vorschlaege.length > 0 && (
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                        {vorschlaege.map(v => {
                            const uebernommen = !!v.uebernommenAm;
                            const beschaeftigt = laeuft === v.id;
                            return (
                                <article
                                    key={v.id}
                                    aria-label={`${v.code}${uebernommen ? ' (übernommen)' : ''}`}
                                    className={`border rounded-lg p-3 flex flex-col gap-2 ${uebernommen ? 'border-slate-100 bg-slate-50 opacity-60' : 'border-slate-200 bg-white'}`}
                                >
                                    <div className="flex items-center justify-between gap-2">
                                        <span className="text-[11px] font-bold uppercase tracking-wider text-emerald-700 bg-emerald-50 border border-emerald-100 px-2 py-0.5 rounded">
                                            {v.code}
                                        </span>
                                        {uebernommen && (
                                            <span className="text-[11px] font-medium text-slate-500 inline-flex items-center gap-1">
                                                <CheckCircle2 className="w-3.5 h-3.5" /> Übernommen am {datumDeutsch(v.uebernommenAm) || '—'}
                                            </span>
                                        )}
                                    </div>
                                    <p className="text-xs text-slate-500 leading-snug">{v.frage}</p>
                                    <p className="text-sm text-slate-800 whitespace-pre-wrap break-words">{v.antwort}</p>
                                    {v.rechtsgrundlage.length > 0 && (
                                        <p className="text-[11px] text-slate-400">{v.rechtsgrundlage.join(' · ')}</p>
                                    )}
                                    {!isReadOnly && (
                                        <div className="flex flex-wrap gap-2 pt-1 mt-auto">
                                            <button
                                                type="button"
                                                onClick={() => void uebernehmen(v)}
                                                disabled={gesperrt}
                                                title={uebernommen
                                                    ? 'Noch einmal als Eintrag vorbelegen (der Stempel bleibt)'
                                                    : 'Eintrag unten vorbelegen und die Frage als übernommen stempeln'}
                                                className="px-2.5 py-1.5 text-xs font-bold text-blue-700 bg-blue-50 hover:bg-blue-100 disabled:opacity-50 rounded-md inline-flex items-center gap-1.5"
                                            >
                                                {beschaeftigt ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
                                                {uebernommen ? 'Erneut als Eintrag vorbelegen' : 'Als Eintrag übernehmen'}
                                            </button>
                                            {!uebernommen && (
                                                <button
                                                    type="button"
                                                    onClick={() => erledigt(v)}
                                                    disabled={gesperrt}
                                                    title="Nur als übernommen stempeln — kein Eintrag"
                                                    className="px-2.5 py-1.5 text-xs font-medium text-slate-600 bg-white border border-slate-200 hover:bg-slate-100 disabled:opacity-50 rounded-md inline-flex items-center gap-1.5"
                                                >
                                                    <EyeOff className="w-3.5 h-3.5" /> Erledigt / ausblenden
                                                </button>
                                            )}
                                        </div>
                                    )}
                                </article>
                            );
                        })}
                    </div>
                )}
            </div>
        </section>
    );
};
